import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import WarningIcon from '@material-ui/icons/Warning';

import ItemsIcon from '../../../assets/icons/ItemIcon';
import AutoFillIcon from '../../../assets/icons/AutoFillIcon';

import Modal from '../../shared/modal';
import Button from '../../shared/Button';
import Input from '../../shared/Input';
import ActionIconButton from '../../shared/ActionIconButton';

import { useAlert } from '../../../hooks';
import { evaluateAllocationScan, GS1_ALLOCATION_POLICY } from '../../../utils/gs1Allocation';
import { createScannerListener } from '../../../utils/scannerInput';

const DATE_STATES = {
  ok: { icon: '✓', className: 'ok' },
  short: { icon: '!', className: 'warn' },
  expired: { icon: '✕', className: 'bad' },
  none: { icon: '–', className: 'none' },
};

const SCAN_WARNING_ICONS = {
  warning: WarningIcon,
  error: ErrorOutlineIcon,
  critical: ReportProblemOutlinedIcon,
};

const CaseConsumablesModal = (props) => {
  const {
    open,
    onClose,
    onUnassignClick,
    onSubmit,
    loading,
    unassignLoading,
    itemId,
    items = [],
    initialConsumables,
    quantity,
  } = props;

  const [consumables, setConsumables] = useState(initialConsumables || []);
  const [scanStates, setScanStates] = useState([]);
  const [scanWarning, setScanWarning] = useState(null);
  const [flash, setFlash] = useState(null);

  const inputRefs = useRef([]);
  const consumablesRef = useRef(consumables);
  const handleScanRef = useRef(() => {});
  const flashTimerRef = useRef(null);

  const { showAlert } = useAlert();

  const item = useMemo(() => {
    const allocationItem = items.find((candidate) => candidate.id === itemId);
    return allocationItem || {};
  }, [itemId, items]);

  useEffect(() => {
    consumablesRef.current = consumables;
  }, [consumables]);

  useLayoutEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        inputRefs.current[0]?.focus();
      });
    }
  }, [open]);

  useEffect(() => {
    const values = initialConsumables && initialConsumables.length
      ? initialConsumables
      : Array(quantity).fill('');

    setConsumables(values);
    consumablesRef.current = values;
    setScanStates(Array(values.length).fill(null));
    setScanWarning(null);
    setFlash(null);
    inputRefs.current = inputRefs.current.slice(0, values.length);
  }, [open, quantity]);

  useEffect(() => () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  const focusNextEmpty = useCallback((values = consumablesRef.current) => {
    const index = values.findIndex((value) => !value?.trim());
    if (index === -1) return;
    requestAnimationFrame(() => inputRefs.current[index]?.focus());
  }, []);

  const dismissWarning = useCallback(() => {
    setScanWarning(null);
    focusNextEmpty();
  }, [focusNextEmpty]);

  useEffect(() => {
    if (!scanWarning) return undefined;

    const onKeydown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        dismissWarning();
      }
    };

    document.addEventListener('keydown', onKeydown, true);
    return () => document.removeEventListener('keydown', onKeydown, true);
  }, [scanWarning, dismissWarning]);

  const showRowFlash = (index, type) => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlash({ index, type });
    flashTimerRef.current = setTimeout(() => setFlash(null), 1200);
  };

  const getGtinDetail = (scanResult) => {
    const { decoded, gtinMatches } = scanResult;
    const itemName = item.checklistDescription || item.description || item.code || 'this item';

    if (gtinMatches) return null;
    if (!item.gtin) {
      return `No GTIN is stored for ${item.code || itemName}, so the scanned product cannot be verified.`;
    }
    if (!decoded.gtin) {
      return `The barcode contains no GTIN, so it cannot be verified against ${item.code || itemName}.`;
    }
    if (decoded.gtinValid === false) {
      return `The scanned GTIN ${decoded.gtin} has an invalid check digit and may be damaged.`;
    }

    const otherItem = items.find((candidate) => candidate.gtin === decoded.gtin);
    if (otherItem) {
      const otherName = otherItem.checklistDescription || otherItem.description || otherItem.code;
      return `The scanned item is ${otherItem.code} — ${otherName}. This allocation is for ${item.code} — ${itemName}.`;
    }

    return `The scanned GTIN ${decoded.gtin} does not match ${item.code || itemName}.`;
  };

  const createRowState = (scanResult) => {
    const { decoded, expiry, gtinMatches, blocked } = scanResult;
    const expiryDisplay = decoded.expiry?.display;
    const dateTitles = {
      ok: `In date — expires ${expiryDisplay} (${expiry.daysLeft} days)`,
      short: `Short dated — expires ${expiryDisplay} (${expiry.daysLeft} days)`,
      expired: `Expired ${expiryDisplay}`,
      none: 'No expiry date in barcode',
    };

    return {
      date: expiry.state,
      dateTitle: dateTitles[expiry.state],
      gtin: gtinMatches ? 'ok' : 'bad',
      gtinTitle: gtinMatches ? 'GTIN verified for this product' : getGtinDetail(scanResult),
      critical: expiry.state === 'expired',
      blocked,
    };
  };

  handleScanRef.current = (raw) => {
    const scanResult = evaluateAllocationScan(raw, item.gtin);
    const {
      decoded,
      value,
      valueKind,
      expiry,
      gtinMatches,
      unreadable,
    } = scanResult;
    const expiryDisplay = decoded.expiry?.display;
    const gtinDetail = getGtinDetail(scanResult);

    if (unreadable) {
      showAlert('warning', "Couldn't read barcode. Try scanning again, or type the LOT manually.");
      return;
    }

    if (!value) {
      if (expiry.state === 'expired') {
        setScanWarning({
          variant: 'critical',
          title: 'Expired product',
          message: `This item expired on ${expiryDisplay}. Nothing was added because the barcode contains no LOT or serial number.`,
        });
      } else if (!gtinMatches && decoded.gtin) {
        setScanWarning({
          variant: 'error',
          title: 'Wrong product scanned',
          message: `${gtinDetail} Nothing was added because the barcode contains no LOT or serial number.`,
        });
      } else if (gtinMatches) {
        showAlert(
          expiry.state === 'short' ? 'warning' : 'success',
          expiry.state === 'short'
            ? `Product verified, but no LOT or serial was found. Short dated: expires ${expiryDisplay}.`
            : 'Product verified, but the barcode contains no LOT or serial number. Enter it manually.',
        );
      } else {
        showAlert('warning', 'Barcode read, but it contains no product, LOT, or serial information.');
      }
      return;
    }

    const values = consumablesRef.current;
    const targetIndex = values.findIndex((consumable) => !consumable?.trim());
    if (targetIndex === -1) {
      showAlert('warning', `All ${values.length} allocation lines are filled. Clear one before scanning again.`);
      return;
    }

    const nextValues = [...values];
    nextValues[targetIndex] = value;
    consumablesRef.current = nextValues;
    setConsumables(nextValues);
    setScanStates((current) => {
      const nextStates = Array(nextValues.length).fill(null);
      current.forEach((state, index) => {
        if (index < nextStates.length) nextStates[index] = state;
      });
      nextStates[targetIndex] = createRowState(scanResult);
      return nextStates;
    });

    const successful = gtinMatches && expiry.state !== 'expired';
    showRowFlash(targetIndex, successful ? 'ok' : 'bad');

    if (expiry.state === 'expired') {
      const daysAgo = Math.abs(expiry.daysLeft);
      setScanWarning({
        variant: 'critical',
        title: 'Expired product',
        message: `Expired ${expiryDisplay} (${daysAgo} day${daysAgo === 1 ? '' : 's'} ago). ${valueKind} ${value} must not be shipped or used.${gtinDetail ? ` It is also the wrong product: ${gtinDetail}` : ''}`,
      });
    } else if (gtinDetail) {
      setScanWarning({
        variant: 'error',
        title: 'Wrong product scanned',
        message: `${gtinDetail}${expiry.state === 'short' ? ` It is also short dated and expires ${expiryDisplay} (${expiry.daysLeft} days).` : ''}`,
      });
    } else if (expiry.state === 'short') {
      setScanWarning({
        variant: 'warning',
        title: 'Short dated product',
        message: `This item expires on ${expiryDisplay}, in ${expiry.daysLeft} day${expiry.daysLeft === 1 ? '' : 's'}.`,
      });
    } else {
      focusNextEmpty(nextValues);
    }
  };

  useEffect(() => {
    if (!open) return undefined;

    const scanner = createScannerListener(
      (raw) => handleScanRef.current(raw),
      {
        onLeakCleanup: (cleanedInputs) => {
          setConsumables((current) => {
            const next = [...current];
            cleanedInputs.forEach(({ target, value }) => {
              const index = inputRefs.current.indexOf(target);
              if (index !== -1) next[index] = value;
            });
            consumablesRef.current = next;
            return next;
          });
        },
      },
    );

    return () => scanner.destroy();
  }, [open]);

  const handleSubmit = async () => {
    await onSubmit(consumables);
  };

  const handleClose = () => {
    setScanWarning(null);
    onClose();
  };

  const onChangeConsumable = (index, value) => {
    const next = [...consumables];
    next[index] = value;
    consumablesRef.current = next;
    setConsumables(next);
    setScanStates((current) => {
      const states = [...current];
      states[index] = null;
      return states;
    });
  };

  const onAutoFillClick = () => {
    if (consumables[0]) {
      const values = Array(quantity).fill(consumables[0]);
      consumablesRef.current = values;
      setConsumables(values);
      setScanStates(Array(values.length).fill(null));
    }
  };

  const hasBlockingScan = scanStates.some((state) => state?.blocked);
  const scanWarningVariant = scanWarning?.variant || 'warning';
  const ScanWarningIcon = SCAN_WARNING_ICONS[scanWarningVariant] || WarningIcon;

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title='Item Allocation'
        fullWidth
      >
        <div className='case-sets-modal__container case-consumables-modal'>
          <div className='d-flex'>
            <ItemsIcon fontSize='small' color='var(--app-text)' />
            <span className='font-bold m-l-md'>{item.checklistDescription || item.description}</span>
            <span className='font-bold m-l-md secondary'>{item.code}</span>
          </div>

          <div className='case-consumables-modal__scanner-note'>
            Scan a GS1 barcode or enter a LOT/serial manually.
          </div>

          <div className='m-t-lg m-b-sm'>
            <div className='d-flex space-between align-end'>
              <div className='field__title m-b-md'>Consumables</div>
              <div className='p-b-md'>
                <ActionIconButton
                  color='blue'
                  disabled={!consumables[0]}
                  tooltip='Auto-fill'
                  onClick={onAutoFillClick}
                >
                  <AutoFillIcon />
                </ActionIconButton>
              </div>
            </div>

            <div className='case-consumables-modal__validation-header' aria-hidden='true'>
              <span />
              <span>DATE</span>
              <span>GTIN</span>
            </div>

            {consumables?.map((consumable, index) => {
              const state = scanStates[index];
              const dateState = state ? DATE_STATES[state.date] : null;
              const flashClass = flash?.index === index
                ? `case-consumables-modal__row--flash-${flash.type}`
                : '';

              return (
                <div
                  key={index}
                  className={[
                    'case-consumables-modal__row',
                    state?.critical ? 'case-consumables-modal__row--critical' : '',
                    flashClass,
                  ].filter(Boolean).join(' ')}
                >
                  <Input
                    ref={(element) => {
                      inputRefs.current[index] = element;
                    }}
                    value={consumable}
                    type='text'
                    onChange={(event) => onChangeConsumable(index, event.target.value)}
                    placeholder={`${index + 1}. Add LOT or Serial No.`}
                    marginBottom={0}
                    error={state?.critical}
                    warning={state?.date === 'short'}
                  />

                  <span
                    className={`case-consumables-modal__validation case-consumables-modal__validation--${dateState?.className || 'empty'}`}
                    title={state?.dateTitle || 'Not scanned'}
                    aria-label={`Expiry: ${state?.dateTitle || 'not validated'}`}
                  >
                    {dateState?.icon || ''}
                  </span>

                  <span
                    className={`case-consumables-modal__validation case-consumables-modal__validation--${state?.gtin || 'empty'}`}
                    title={state?.gtinTitle || 'Not scanned'}
                    aria-label={`GTIN: ${state?.gtinTitle || 'not validated'}`}
                  >
                    {state ? (state.gtin === 'ok' ? '✓' : '✕') : ''}
                  </span>
                </div>
              );
            })}
          </div>

          <div className='form__actions p-t-lg'>
            <Button
              type='submit'
              text='Confirm'
              onClick={handleSubmit}
              disabled={loading || hasBlockingScan || consumables?.some((value) => !value)}
              loading={loading}
            />

            <Button
              type='outlined'
              text='Unassign'
              onClick={onUnassignClick}
              disabled={unassignLoading}
              loading={unassignLoading}
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={!!scanWarning}
        onClose={dismissWarning}
        fullWidth
        size='xs'
        showCloseButton={false}
        className={`case-scan-warning-dialog case-scan-warning-dialog--${scanWarningVariant}`}
      >
        <div className='case-scan-warning' role='alert'>
          <div className='case-scan-warning__title'>
            <span className='case-scan-warning__icon' aria-hidden='true'>
              <ScanWarningIcon />
            </span>
            <span>{scanWarning?.title}</span>
          </div>
          <div className='case-scan-warning__message'>{scanWarning?.message}</div>
          <Button
            type='submit'
            text='OK'
            onClick={dismissWarning}
          />
          <div className='case-scan-warning__hint'>Press Enter to continue scanning</div>
        </div>
      </Modal>
    </>
  );
};

export { GS1_ALLOCATION_POLICY };
export default CaseConsumablesModal;
